// src/features/billing/billing-summary.ts
import type { Subscription, SubscriptionStatus, UsageSnapshot } from '@/shared/types/billing'

export type BillingTone = 'ok' | 'warning' | 'danger'

export interface BillingSummary {
  tokenBalance: number
  usedPct: number
  renewalDate: string | null
  status: SubscriptionStatus | null
  tone: BillingTone
  hasBalance: boolean
  // Подписка закончилась (см. isEnded) — единый признак для карточки, баннера и чипа.
  ended: boolean
  // Следующее списание запланировано; иначе renewalDate — просто конец оплаченного периода.
  renews: boolean
}

// Единый источник расчёта расхода и тона — чтобы карточка, чип и плашка не расходились по порогам.
export function usedPercent(period: UsageSnapshot['current_billing_period']): number {
  return period && period.granted_tokens > 0
    ? Math.min(100, Math.round((period.used_tokens / period.granted_tokens) * 100))
    : 0
}

// Подписка закончилась: ни продления, ни «следующего списания» у неё нет. Отменённая, но
// оплаченная до будущей даты подписка ещё действует.
export function isEnded(status: SubscriptionStatus | null, periodEnd: string | null): boolean {
  if (status === 'expired') {
    return true
  }
  return status === 'cancelled' && (periodEnd == null || new Date(periodEnd) <= new Date())
}

export function toneFor(status: SubscriptionStatus | null, usedPct: number): BillingTone {
  if (status === 'past_due' || status === 'expired' || usedPct >= 90) {
    return 'danger'
  }
  if (usedPct >= 70) {
    return 'warning'
  }
  return 'ok'
}

export function deriveBillingSummary(
  usage: UsageSnapshot | null,
  subscription: Subscription | null,
): BillingSummary {
  const hasBalance = usage != null
  const tokenBalance = usage ? usage.subscription_token_balance + usage.topup_token_balance : 0
  const usedPct = usedPercent(usage?.current_billing_period ?? null)
  const status = subscription?.status ?? null
  const stopped = status === 'expired' || status === 'cancelled'
  const renewalDate = subscription
    ? stopped
      ? subscription.current_period_end
      : (subscription.next_renewal_at ?? subscription.current_period_end)
    : null
  const ended = subscription ? isEnded(status, subscription.current_period_end) : false

  return {
    tokenBalance,
    usedPct,
    renewalDate,
    status,
    tone: toneFor(status, usedPct),
    hasBalance,
    ended,
    renews: !stopped && subscription?.next_renewal_at != null,
  }
}

export function formatRenewalDate(iso: string | null, opts?: { withYear?: boolean }): string {
  if (!iso) {
    return '—'
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    ...(opts?.withYear ? { year: 'numeric' } : {}),
  }).format(date)
}
